import { createSelector } from 'reselect';
import truncate from 'lodash/truncate';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';

import { AGENCY_BRANDS, BRAND, MY_BRANDS } from 'utils/brands';
import { getEntityFromOrganization } from 'utils/view';

import { __t, applyTranslations } from 'utils/language';
import type { Agency, InfluencerMeta, InfluencerMetaKey, Organization } from 'types';

applyTranslations(require('./brand-filters.i18n.json'));

type MapByAgencyOrganizationIdType = {
  [x: number]: boolean
};

type State = {
  brandsFilter: {
    activeFilter: string
  },
  organizations: {
    list: Array<Organization>,
    currentOrganization: Organization
  },
  account: {
    mapByAgencyOrganizationId: MapByAgencyOrganizationIdType,
    currentAccount: {
      agency: Agency
    }
  }
};

const MAX_LENGTH_TAB_NAME = 35;

const getBrandOrganizationsCount = organizations => (organizations || []).filter(o => o.type === 'brand').length;

const currentOrganizationSelector = (
  {
    organizations
  }: State
): Organization => organizations.currentOrganization;

const brandOrganizationsCountSelector = (
  {
    organizations
  }: State
): number => getBrandOrganizationsCount(organizations.list);

const mapByAgencyOrganizationIdSelector = (
  {
    account
  }: State
): MapByAgencyOrganizationIdType => account.mapByAgencyOrganizationId;

const agencySelector = (
  {
    account
  }: State
): Agency => account.currentAccount.agency;

// $FlowIgnore - Create selector uses some specific TState, TResult types
export const availableBrandFiltersSelector = createSelector(
  [currentOrganizationSelector, brandOrganizationsCountSelector, mapByAgencyOrganizationIdSelector],
  (
    currentOrganization: Organization,
    brandOrganizationsCount: number,
    mapByAgencyOrganizationId: MapByAgencyOrganizationIdType
  ): Array<string> => {
    const hasAgency = mapByAgencyOrganizationId[currentOrganization.id];
    const filters = [BRAND];

    if (brandOrganizationsCount > 1) {
      filters.push(MY_BRANDS);
    }

    if (hasAgency) {
      filters.push(AGENCY_BRANDS);
    }

    return filters;
  }
);

// $FlowIgnore - Dynamic method selection is being used in `availableBrandTabsSelector` below
function getLabel(value, entity, agency) {
  return __t(`brands_filter.options.label_${value}`, {
    organization: truncate(entity.displayName, { length: MAX_LENGTH_TAB_NAME }),
    agency: (agency || {}).name
  });
}

function getLabelWithCount(value, entity, agency, influencerMeta, metaKey) {
  const count = get(influencerMeta, [metaKey, camelCase(value)], []).length;
  return `${getLabel(value, entity, agency)} (${count})`;
}

// $FlowIgnore - Create selector uses some specific TState, TResult types
export const availableBrandFilterOptionsSelector = createSelector(
  [availableBrandFiltersSelector, currentOrganizationSelector, agencySelector],
  (teams, currentOrganization, agency) => {
    const entity = getEntityFromOrganization(currentOrganization);
    return teams.map(value => ({
      label: getLabel(value, entity, agency),
      value
    }));
  }
);

const influencerMetaFromPropsSelector = (state, { influencerMeta }): InfluencerMeta => influencerMeta;
const metaKeyFromPropsSelector = (state, { metaKey }): InfluencerMetaKey => metaKey;
const hideCountFromPropsSelector = (state, { hideCount }): boolean | undefined | null => hideCount;

// $FlowIgnore - Create selector uses some specific TState, TResult types
export const availableBrandTabsSelector = createSelector(
  [
    availableBrandFiltersSelector,
    currentOrganizationSelector,
    agencySelector,
    influencerMetaFromPropsSelector,
    metaKeyFromPropsSelector,
    hideCountFromPropsSelector
  ],
  (brandFilters, currentOrganization, agency, influencerMeta, metaKey, hideCount) => {
    const entity = getEntityFromOrganization(currentOrganization);
    const method = hideCount ? getLabel : getLabelWithCount;

    return brandFilters.map(value => ({
      identifier: value,
      // $FlowIgnore - Cannot call method because no more than 3 arguments are expected by function
      label: method(value, entity, agency, influencerMeta, metaKey)
    }));
  }
);

export const getActiveBrandFilterSelector = ({
  brandsFilter
}: State) => brandsFilter.activeFilter;
